import React, { useEffect, useId, useRef, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { createPortal } from "react-dom";
import styles from "./CascadingDropdown.module.scss";
import { ReactComponent as ArrowNextIcon } from "../../icons/arrow-next.svg";
import { ReactComponent as DownArrowIcon } from "../../icons/down-arrow.svg";
import { ReactComponent as UpArrowIcon } from "../../icons/up-arrow.svg";

const CascadingDropdown = ({
  label,
  text,
  field,
  form,
  className,
  disabled,
  options = [],
  suboptions = [],
  selectedValues = [],
  disableSelected = false,
  initialHoveredOption = null,
  usePortal = true,
  openDirection = "down",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);
  const [lockedOption, setLockedOption] = useState(null);
  // Track portal position so the dropdown can escape clipping parents
  const [portalStyle, setPortalStyle] = useState(null);
  const dropdownId = useId();
  const triggerRef = useRef(null);
  const optionsRef = useRef({});

  useEffect(() => {
    if (!isOpen || !initialHoveredOption) return;

    // Scroll only once *on open*, not on every hover
    const timeout = setTimeout(() => {
      const el = optionsRef.current[initialHoveredOption];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 50); // slight delay to ensure render

    return () => clearTimeout(timeout);
  }, [isOpen, initialHoveredOption]);

  useEffect(() => {
    if (initialHoveredOption) {
      setHoveredOption(initialHoveredOption);
    }
  }, [initialHoveredOption]);

  const updatePortalPosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 2;
    const openUpwards = openDirection === "up";
    setPortalStyle({
      position: "fixed",
      top: openUpwards ? undefined : rect.bottom + margin,
      bottom: openUpwards ? window.innerHeight - rect.top + margin : undefined,
      left: rect.left,
      width: rect.width,
      zIndex: 12000,
    });
  };

  useEffect(() => {
    if (!isOpen || !usePortal) return;
    updatePortalPosition();

    const handleReposition = () => updatePortalPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen, usePortal]);

  useEffect(() => {
    if (!isOpen) {
      // Reset locked option when dropdown closes
      setLockedOption(null);
      return;
    }

    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      if (key.length === 1 && key.match(/[a-z]/)) {
        const match = options.find((opt) =>
          opt.label.toLowerCase().startsWith(key),
        );
        if (match) {
          setHoveredOption(match.id);
          const el = optionsRef.current[match.id];
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, options]);

  const selectedOptionLabel =
    suboptions.find(
      (sub) => String(sub.id) === String(field.value ?? ""),
    )?.label || text;

  const optionToSuboptions = options.reduce((acc, option) => {
    acc[option.id] = suboptions.filter((sub) =>
      sub.reference.startsWith(option.id),
    );
    return acc;
  }, {});

  const handleSelect = (option) => {
    form.setFieldValue(field.name, option.value);
    setIsOpen(false);
    setLockedOption(null);
  };

  const handleOptionClick = (optionId) => {
    if (lockedOption === optionId) {
      // If clicking on already locked option, unlock it
      setLockedOption(null);
    } else {
      // Lock the clicked option
      setLockedOption(optionId);
      setHoveredOption(optionId);
    }
  };

  const handleOptionHover = (optionId) => {
    // Only update hover if no option is locked
    if (lockedOption === null) {
      setHoveredOption(optionId);
    }
  };

  // Use locked option if available, otherwise use hovered option
  const activeOption = lockedOption || hoveredOption;

  const dropdownContent = (
    <div
      className={`${styles.dropdownList} ${usePortal ? styles.portalDropdown : ""}`}
      data-dropdown-portal-id={dropdownId}
      data-ignore-outside-click
      style={usePortal ? portalStyle || undefined : undefined}
    >
      <div
        className={`${styles.optionsColumn} ${
          activeOption ? styles.active : ""
        }`}
      >
        {options.map((option) => {
          const isLocked = lockedOption === option.id;
          const isHovered = hoveredOption === option.id;

          return (
            <div
              key={option.id}
              ref={(el) => (optionsRef.current[option.id] = el)}
              className={`${styles.dropdownItem} ${
                isHovered ? styles.highlighted : ""
              } ${isLocked ? styles.locked : ""}`}
              onMouseEnter={() => handleOptionHover(option.id)}
              onClick={() => handleOptionClick(option.id)}
            >
              {option.label}
              <ArrowNextIcon />
            </div>
          );
        })}
      </div>
      {activeOption && (
        <div
          className={`${styles.suboptionsColumn} ${
            activeOption ? styles.active : ""
          }`}
        >
          {optionToSuboptions[activeOption]?.map((sub) => {
            const isDisabled =
              disableSelected &&
              selectedValues.some(
                (value) => String(value) === String(sub.id),
              );

            return (
              <div
                key={sub.id}
                className={`${styles.subDropdownItem} ${
                  isDisabled ? styles.disabledItem : ""
                }`}
                onClick={() =>
                  !isDisabled &&
                  handleSelect({ value: sub.id, label: sub.label })
                }
              >
                {sub.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <OutsideClickHandler
      onOutsideClick={(event) => {
        if (
          usePortal &&
          event?.target?.closest?.(
            `[data-dropdown-portal-id="${dropdownId}"]`,
          )
        ) {
          return;
        }
        setIsOpen(false);
      }}
    >
      <div className={`${styles.dropdownContainer} ${className || ""}`}>
        {label && <label>{label}</label>}
        <div
          className={`${styles.dropdownTrigger} ${disabled ? styles.disabled : ""} ${isOpen && styles.active}`}
          ref={triggerRef}
          onClick={() => {
            if (!disabled) {
              const next = !isOpen;
              if (next && initialHoveredOption) {
                setHoveredOption(initialHoveredOption);
              }
              if (next && usePortal) {
                updatePortalPosition();
              }
              setIsOpen(next);
            }
          }}
        >
          {selectedOptionLabel}
          <span className={styles.icon}>
            {isOpen ? <UpArrowIcon /> : <DownArrowIcon />}
          </span>
        </div>

        {isOpen &&
          (usePortal
            ? createPortal(dropdownContent, document.body)
            : dropdownContent)}
      </div>
    </OutsideClickHandler>
  );
};

export default CascadingDropdown;
